import {ImportModuleTargetField} from '../../core/definitions/import-module';
import {ObjectTypes} from '../../core/definitions/object-types';
import {GroupKind, GroupSubBlockRow, namedTakesNames} from './group-card/group-card.model';
import {CREATED_AT_TARGET, CREATED_BY_TARGET} from './import-payload';
import {TemplatePart} from './value-template';

/**
 * Where the mapping reads each source column: which flat rows and card
 * members take a column's cells, directly or through a lookup, and what
 * each does with a value.
 */

/** What a field does with a value that is not read as blank: a concept
 * field looks it up in its list, a Named sub-block links an Agent or a
 * NamedObject by that name, any other field takes it as text. `other` is
 * a field none of these tells about (a Place, a KulturNav id). */
export type BlankUsageKind = 'concept' | 'agent' | 'named_object' | 'field' | 'other';

/** What a field's usage kind is read from. */
type UsageField = Pick<ImportModuleTargetField, 'input_type' | 'group_concept'>;

// Inputs that pick a record rather than a term of a list.
const REFERENCE_INPUT_TYPES = new Set(['search_selector', 'search_selector_multiple', 'ref_array']);

/** What a mapping row or card member does with a value: `concept` for a
 * field whose value names a term of a list - a group's concept member, or
 * a `map_id` input - `other` for one that takes a KulturNav id or picks a
 * record by another input, and `field` for the rest. */
export function fieldUsageKind(
  field: UsageField | undefined, kulturNavUuid: boolean,
): BlankUsageKind {
  if (!field) {
    return 'field';
  }
  if (kulturNavUuid || REFERENCE_INPUT_TYPES.has(field.input_type)) {
    return 'other';
  }
  return field.group_concept || field.input_type === 'map_id' ? 'concept' : 'field';
}

// Inputs that take a date.
const DATE_INPUT_TYPES = new Set(['date_iso', 'date_time_iso']);

// The type tags of the registration date and the registrar, which their
// `text` input does not give. The registrar keeps that input because a
// record-picker one would make `fieldUsageKind` read it as `other`.
const TARGET_TYPE_TAG_KEYS: ReadonlyMap<string, string> = new Map([
  [CREATED_AT_TARGET, 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_DATE'],
  [CREATED_BY_TARGET, 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_REFERENCE'],
]);

/** The label key of a field's type tag in the add search: the registration
 * date's «Dato» and the registrar's «Referanse»; for any other field,
 * «Listeverdi» for one `fieldUsageKind` reads as a concept, «Referanse» for
 * a record picker, «Dato» for a date input, and «Tekst» for the rest. */
export function fieldTypeTagKey(field: UsageField & Partial<Pick<ImportModuleTargetField, 'field_id'>>): string {
  const targetTag = field.field_id === undefined ? undefined : TARGET_TYPE_TAG_KEYS.get(field.field_id);
  if (targetTag) {
    return targetTag;
  }
  switch (fieldUsageKind(field, false)) {
    case 'concept':
      return 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_CONCEPT';
    case 'other':
      return 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_REFERENCE';
    default:
      return DATE_INPUT_TYPES.has(field.input_type)
        ? 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_DATE'
        : 'TRANS__IMPORT__MAPPING__ADD_SEARCH_TYPE_TEXT';
  }
}

/** `fieldUsageKind` for a member of a `groupKind` card, `field` being
 * from that kind's own field list. The Fast plassering card's Location
 * member finds a Location by its code path rather than a term, so it is
 * `other`. */
export function memberUsageKind(
  groupKind: GroupKind, field: UsageField | undefined, kulturNavUuid: boolean,
): BlankUsageKind {
  const kind = fieldUsageKind(field, kulturNavUuid);
  return groupKind === 'placement_event' && kind === 'concept' ? 'other' : kind;
}

/** What a Named sub-block does with a name read from a column: links an
 * Agent or a NamedObject by it. A Place sub-block, and a Named one taking
 * KulturNav ids or a fixed pick, is `other`. */
export function subBlockUsageKind(sub: GroupSubBlockRow): BlankUsageKind {
  if (!namedTakesNames(sub)) {
    return 'other';
  }
  return sub.target!.objectTypeControl.value === ObjectTypes.AGENT ? 'agent' : 'named_object';
}

/** A mapping row or card member, as the usages read it. */
export interface UsageSource {
  // "Tittel 1 · Språk": the field, and the card and sub-block it is on.
  label: string;
  // The field's own label.
  field: string;
  fieldId: string;
  kind: BlankUsageKind;
  parts: readonly TemplatePart[];
}

/** One place the mapping reads a column. */
export interface ColumnUsage extends Omit<UsageSource, 'parts'> {
  // The column reaches the field through a lookup, so the field gets the
  // lookup's label, not the code.
  viaLookup: boolean;
  // The key of that lookup.
  lookup?: string;
  // The field takes the column's cell as it stands, and nothing else.
  whole: boolean;
}

/** The usages of each column `sources` read: a `{Column}` or compose
 * line directly, or through a lookup part, which reads the columns
 * `lookupColumns` gives for its key. */
export function columnUsages(
  sources: readonly UsageSource[], lookupColumns: ReadonlyMap<string, readonly string[]>,
): Map<string, ColumnUsage[]> {
  const usages = new Map<string, ColumnUsage[]>();
  for (const {parts, ...source} of sources) {
    const whole = parts.length === 1 && parts[0].kind === 'column';
    const read = new Set<string>();
    for (const part of parts) {
      const direct = part.kind === 'column' || part.kind === 'conditional';
      const columns = direct ? [part.value] : part.kind === 'lookup' ? lookupColumns.get(part.value) ?? [] : [];
      for (const column of columns.filter(c => !read.has(c))) {
        read.add(column);
        const usage: ColumnUsage = {...source, viaLookup: !direct, whole};
        if (!direct) {
          usage.lookup = part.value;
        }
        usages.set(column, [...(usages.get(column) ?? []), usage]);
      }
    }
  }
  return usages;
}

/** The labels of `usages`, as one list. */
export function usagesText(usages: readonly ColumnUsage[] | undefined): string {
  return (usages ?? []).map(usage => usage.label).join(', ');
}
